import React from "react";
import { Box, Stack } from "@mui/joy";
import DarkTypography from "components/common/DarkTypography";
import PersonChip from "components/common/PersonChip";
import {
  colorTokens,
  excursionClientChipDraggableSx,
  excursionClientChipReadonlySx,
  excursionDropTargetEmptySx,
  excursionGroupHeaderDividerBorder,
  excursionPersonChipOnGradientSx,
  excursionTooltipMutedTextColor,
  excursionTooltipMutedTextSoftColor,
  excursionTooltipParamLabelColorSoft,
  withAlpha,
} from "styles/globalStyles";
import { GROUP_DEFAULT_COLOR } from "utils/colorPalette";
import { userDisplayName } from "utils/userHelper";
import { normalizeCustomerEmoji, rawCustomerEmojiFromApi } from "../../../common/customerEmoji";
import { normalizeBookingStatusForPersonChip } from "../../../excursions/shared/utils/excursionUtils";
import type {
  BookingStatus,
  Excursion,
  ExcursionChipDropPending,
  ExcursionGroup,
  GroupLabels,
} from "api/excursions/Excursion.typed";
import { normalizeMultiCommentList } from "api/zekay/comments";
import { groupTitle } from "./excursionTooltipHelpers";
import ExcursionTooltipParamEntry from "./ExcursionTooltipParamEntry";
import DarkCard from "components/common/DarkCard";
import ExcursionDropPendingOverlay from "./ExcursionDropPendingOverlay";

/** Planning drawer passes this as `groupSurface` — one DarkCard per groupe. */
export const EXCURSION_GROUPS_SURFACE_DRAWER = "drawerCards" as const;

function ExcursionTooltipGroupOuter(props: {
  variant: "tooltip" | "drawerCards";
  groupColor: string;
  reconnectDropHit: boolean;
  dropHandlers: object | undefined;
  dropLoading?: boolean;
  children: React.ReactNode;
}) {
  const { variant, groupColor, reconnectDropHit, dropHandlers, dropLoading, children } = props;
  const baseDrop = reconnectDropHit ? { pointerEvents: "auto" as const } : {};

  if (variant === EXCURSION_GROUPS_SURFACE_DRAWER) {
    return (
      <DarkCard
        {...(dropHandlers ?? {})}
        dense
        sx={{
          position: "relative",
          py: 0.75,
          px: { xs: 1, sm: 1.25 },
          borderLeft: `4px solid ${groupColor}`,
          background: `linear-gradient(135deg, ${colorTokens.bg.surface} 0%, ${withAlpha(groupColor, 0.12)} 140%)`,
          boxShadow: `0 2px 4px ${withAlpha(colorTokens.bg.overlay, 0.45)}`,
          ...baseDrop,
        }}
      >
        {children}
        <ExcursionDropPendingOverlay show={Boolean(dropLoading)} />
      </DarkCard>
    );
  }

  return (
    <Box
      {...(dropHandlers ?? {})}
      sx={{
        position: "relative",
        borderRadius: 6,
        p: 0.75,
        minHeight: 44,
        border: `1px solid ${colorTokens.border.default}`,
        borderLeft: `4px solid ${groupColor}`,
        backgroundColor: `${groupColor}14`,
        ...baseDrop,
      }}
    >
      {children}
      <ExcursionDropPendingOverlay show={Boolean(dropLoading)} />
    </Box>
  );
}

export type ExcursionTooltipGroupsBodyProps = {
  excursion: Excursion;
  groups: ExcursionGroup[];
  groupLabels?: GroupLabels[];
  groupDropHandlers?: (targetExcursionId: number, targetGroupIndex: number) => object;
  canEdit: boolean;
  bookingStatusMap?: Record<number, BookingStatus>;
  onClientDragStart?: (sourceExcursionId: number) => void;
  onClientDragEnd?: () => void;
  /** When false, chips are not HTML5 drag sources (tooltip planning: sources only from drawer). */
  clientChipDragSourceEnabled?: boolean;
  getCustomerInProgressTrainingsTooltip?: (customerId: number) => string | null;
  /** When excursion floater uses pointer-events:none on the Popper, set on drop/chip hit targets so HTML5 D&D still works. */
  reconnectDropSurfacePointerEventsInsideFloater?: boolean;
  /** When set, each group is wrapped in DarkCard (planning side drawer). */
  groupSurface?: "tooltip" | "drawerCards";
  chipDropPending?: ExcursionChipDropPending | null;
};

export default function ExcursionTooltipGroupsBody({
  excursion,
  groups,
  groupLabels,
  groupDropHandlers,
  canEdit,
  bookingStatusMap,
  onClientDragStart,
  onClientDragEnd,
  clientChipDragSourceEnabled = true,
  getCustomerInProgressTrainingsTooltip,
  reconnectDropSurfacePointerEventsInsideFloater = false,
  groupSurface = "tooltip",
  chipDropPending = null,
}: ExcursionTooltipGroupsBodyProps) {
  const reconnectDropHit = reconnectDropSurfacePointerEventsInsideFloater;
  const chipDragSourceHere = canEdit && clientChipDragSourceEnabled;
  const personChipRowSpacing = groupSurface === EXCURSION_GROUPS_SURFACE_DRAWER ? 1 : 2;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: groupSurface === EXCURSION_GROUPS_SURFACE_DRAWER ? 1.5 : 1,
      }}
    >
      {groups.length === 0 ? (
        groupDropHandlers && groupSurface !== EXCURSION_GROUPS_SURFACE_DRAWER ? (
          <Box
            {...groupDropHandlers(excursion.id, 0)}
            sx={{
              position: "relative",
              ...excursionDropTargetEmptySx,
              ...(reconnectDropHit ? { pointerEvents: "auto" } : {}),
            }}
          >
            <DarkTypography level="body-xs" sx={{ color: excursionTooltipMutedTextSoftColor }}>
              Déposer pour créer un groupe
            </DarkTypography>
            <ExcursionDropPendingOverlay
              show={
                chipDropPending?.excursionId === excursion.id &&
                chipDropPending.surface === "group" &&
                chipDropPending.groupIndex === 0
              }
            />
          </Box>
        ) : (
          <DarkTypography level="body-xs" sx={{ color: excursionTooltipMutedTextColor }}>
            Aucun groupe
          </DarkTypography>
        )
      ) : (
        <>
          {groups.map((g, idx) => {
            const labels = groupLabels?.[idx];
            const groupColor = g.color && String(g.color).trim() ? g.color : GROUP_DEFAULT_COLOR;
            const customerColor =
              g.excursion_customers?.[0]?.color && String(g.excursion_customers[0].color).trim()
                ? g.excursion_customers[0].color
                : null;
            const chipColor = customerColor ?? groupColor;
            const groupDropLoading =
              chipDropPending?.excursionId === excursion.id &&
              chipDropPending.surface === "group" &&
              chipDropPending.groupIndex === idx;
            return (
              <ExcursionTooltipGroupOuter
                key={g.id ?? idx}
                variant={
                  groupSurface === EXCURSION_GROUPS_SURFACE_DRAWER
                    ? EXCURSION_GROUPS_SURFACE_DRAWER
                    : "tooltip"
                }
                groupColor={groupColor}
                reconnectDropHit={reconnectDropHit}
                dropHandlers={groupDropHandlers ? groupDropHandlers(excursion.id, idx) : undefined}
                dropLoading={groupDropLoading}
              >
                <Box
                  sx={{
                    borderBottom: excursionGroupHeaderDividerBorder,
                    pb: 0.25,
                    mb: 0.25,
                  }}
                >
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    spacing={personChipRowSpacing}
                    useFlexGap
                    alignItems="center"
                    sx={{ minWidth: 0, flex: 1 }}
                  >
                    {(g.excursion_users?.length ?? 0) > 0 ? (
                      g.excursion_users!.map((u) => {
                        const uLabel = labels?.guides?.find((e) => e.id === u.id);
                        const uFirstName = uLabel?.first_name;
                        const uLastName = uLabel?.last_name;
                        const uQualification = uLabel?.qualification;
                        const encadrantChipLabel = !(uFirstName || uLastName)
                          ? (uLabel?.name ?? `#${u.id}`)
                          : undefined;
                        const uColor = u.color && String(u.color).trim() ? u.color : groupColor;
                        const status = bookingStatusMap?.[u.id];
                        const bookingStatusForChip = normalizeBookingStatusForPersonChip(status);
                        const userDragPayload = () => ({
                          type: "user" as const,
                          sourceExcursionId: excursion.id,
                          sourceGroupIndex: idx,
                          user_id: u.id,
                          user_color: uColor,
                        });
                        return (
                          <PersonChip
                            key={u.id}
                            size="sm"
                            variant="soft"
                            firstName={uFirstName}
                            lastName={uLastName}
                            qualification={uQualification}
                            label={encadrantChipLabel}
                            chipColor={uColor}
                            comments={normalizeMultiCommentList(
                              u.comments as unknown[] | undefined,
                            )}
                            commentsImportantTooltipOnly
                            bookingStatus={bookingStatusForChip}
                            draggable={chipDragSourceHere}
                            onDragStart={
                              chipDragSourceHere
                                ? (ev: React.DragEvent) => {
                                    ev.dataTransfer.setData(
                                      "application/json",
                                      JSON.stringify(userDragPayload()),
                                    );
                                    ev.dataTransfer.effectAllowed = "copyMove";
                                    onClientDragStart?.(excursion.id);
                                  }
                                : undefined
                            }
                            onDragEnd={chipDragSourceHere ? () => onClientDragEnd?.() : undefined}
                            sx={
                              canEdit
                                ? {
                                    ...excursionPersonChipOnGradientSx,
                                    ...(chipDragSourceHere ? excursionClientChipDraggableSx : {}),
                                    ...(reconnectDropHit ? { pointerEvents: "auto" as const } : {}),
                                  }
                                : excursionPersonChipOnGradientSx
                            }
                          />
                        );
                      })
                    ) : (
                      <DarkTypography
                        level="body-xs"
                        sx={{ color: excursionTooltipMutedTextColor }}
                      >
                        {groupTitle(g, idx, labels)} — Aucun encadrant
                      </DarkTypography>
                    )}
                  </Stack>
                </Box>
                {(g.excursion_customers?.length ?? 0) > 0 ? (
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    spacing={personChipRowSpacing}
                    useFlexGap
                    alignItems="center"
                  >
                    {(g.excursion_customers ?? []).map((cust, cIdx) => {
                      const cLabel =
                        labels?.customers?.find((l) => l.id === cust.id) ??
                        labels?.customers?.[cIdx];
                      const inProgressTrainingsTooltip =
                        getCustomerInProgressTrainingsTooltip?.(cust.id) ?? null;
                      const custColor =
                        cust.color && String(cust.color).trim() ? cust.color : chipColor;
                      const chipEmoji =
                        normalizeCustomerEmoji(cLabel?.emoji ?? rawCustomerEmojiFromApi(cust)) ??
                        null;
                      const chipLabel = !(cLabel?.first_name || cLabel?.last_name)
                        ? (cLabel?.name ?? `#${cust.id}`)
                        : undefined;
                      const dragPayload = () => ({
                        type: "client",
                        sourceExcursionId: excursion.id,
                        sourceGroupIndex: idx,
                        customer_id: cust.id,
                        customer_label:
                          cLabel?.first_name || cLabel?.last_name
                            ? userDisplayName(cLabel)
                            : (cLabel?.name ?? `#${cust.id}`),
                        customer_color: custColor,
                        ...(chipEmoji ? { customer_emoji: chipEmoji } : {}),
                      });
                      return chipDragSourceHere ? (
                        <PersonChip
                          key={cust.id}
                          size="sm"
                          variant="soft"
                          chipColor={custColor}
                          emoji={chipEmoji}
                          draggable
                          lastName={cLabel?.last_name}
                          firstName={cLabel?.first_name}
                          qualification={cLabel?.qualification}
                          label={chipLabel}
                          inProgressTrainingsTooltip={inProgressTrainingsTooltip}
                          comments={normalizeMultiCommentList(
                            cust.comments as unknown[] | undefined,
                          )}
                          commentsImportantTooltipOnly
                          onDragStart={(ev: React.DragEvent) => {
                            ev.dataTransfer.setData(
                              "application/json",
                              JSON.stringify(dragPayload()),
                            );
                            ev.dataTransfer.effectAllowed = "copyMove";
                            onClientDragStart?.(excursion.id);
                          }}
                          onDragEnd={() => onClientDragEnd?.()}
                          sx={{
                            ...excursionClientChipDraggableSx,
                            ...(reconnectDropHit ? { pointerEvents: "auto" as const } : {}),
                          }}
                        />
                      ) : canEdit ? (
                        <PersonChip
                          key={cust.id}
                          size="sm"
                          variant="soft"
                          chipColor={custColor}
                          emoji={chipEmoji}
                          lastName={cLabel?.last_name}
                          firstName={cLabel?.first_name}
                          qualification={cLabel?.qualification}
                          label={chipLabel}
                          inProgressTrainingsTooltip={inProgressTrainingsTooltip}
                          comments={normalizeMultiCommentList(
                            cust.comments as unknown[] | undefined,
                          )}
                          commentsImportantTooltipOnly
                          sx={{
                            ...excursionPersonChipOnGradientSx,
                            ...(reconnectDropHit ? { pointerEvents: "auto" as const } : {}),
                          }}
                        />
                      ) : (
                        <PersonChip
                          key={cust.id}
                          size="sm"
                          variant="plain"
                          chipColor={custColor}
                          emoji={chipEmoji}
                          lastName={cLabel?.last_name}
                          firstName={cLabel?.first_name}
                          qualification={cLabel?.qualification}
                          label={chipLabel}
                          inProgressTrainingsTooltip={inProgressTrainingsTooltip}
                          comments={normalizeMultiCommentList(
                            cust.comments as unknown[] | undefined,
                          )}
                          commentsImportantTooltipOnly
                          sx={excursionClientChipReadonlySx}
                        />
                      );
                    })}
                  </Stack>
                ) : (
                  <DarkTypography level="body-xs" sx={{ color: excursionTooltipMutedTextColor }}>
                    Aucun client
                  </DarkTypography>
                )}
                {(() => {
                  const groupParams = (g.params ?? {}) as Record<string, unknown>;
                  const groupEntries = Object.entries(groupParams).filter(
                    ([, v]) => v !== undefined && v !== null && v !== "",
                  );
                  return (
                    <Box sx={{ mt: 0.5, display: "flex", flexDirection: "column", gap: 0.25 }}>
                      {groupEntries.length > 0
                        ? groupEntries.map(([key, v]) => (
                            <ExcursionTooltipParamEntry
                              key={key}
                              label={key}
                              value={v}
                              color={excursionTooltipParamLabelColorSoft}
                            />
                          ))
                        : null}
                    </Box>
                  );
                })()}
              </ExcursionTooltipGroupOuter>
            );
          })}
        </>
      )}
    </Box>
  );
}
